import { useAppSelector } from "../redux/hooks";
import { useModuleEnabled } from "../redux/selectors/modules";
import { AD_TYPES, GOALS } from "../components/core/blocks/AdPlan/constants";
import type { TSelector } from "../i18n/types";

export interface TabOption {
  key: string;
  groupKey: string;
  label: TSelector;
  groupLabel: TSelector;
}

const AD_DETAILS_TAB_OPTIONS = {
  PRODUCTS: {
    key: "products",
    groupKey: "products",
    label: ($) => $.advertising["Products"],
    groupLabel: ($) => $.advertising["Products"],
  },
  MEDIA_PLAN: {
    key: "mediaPlan",
    groupKey: "configuration",
    label: ($) => $.advertising["Media Plan"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  KEYWORDS: {
    key: "keywords",
    groupKey: "keyword_targets",
    label: ($) => $.advertising["Keyword Targets"],
    groupLabel: ($) => $.advertising["Keyword Targets"],
  },
  TARGETS: {
    key: "targets",
    groupKey: "product_targets",
    label: ($) => $.advertising["Product Targets"],
    groupLabel: ($) => $.advertising["Product Targets"],
  },
  NEGATIVE_KEYWORDS: {
    key: "negative-keywords",
    groupKey: "negative_keyword_targets",
    label: ($) => $.advertising["Negative Keywords"],
    groupLabel: ($) => $.advertising["Negative Keyword Targets"],
  },
  SERVING_HOURS: {
    key: "servingHours",
    groupKey: "configuration",
    label: ($) => $.advertising["Serving Hours"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  NOTIFICATIONS: {
    key: "notifications",
    groupKey: "configuration",
    label: ($) => $.advertising["Notifications"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  PLACEMENTS: {
    key: "placements",
    groupKey: "placements",
    label: ($) => $.advertising["Placements"],
    groupLabel: ($) => $.advertising["Placements"],
  },
  CAMPAIGN_PLACMENTS: {
    key: "campaignPlacements",
    groupKey: "placement_report",
    label: ($) => $.advertising["Placement Report"],
    groupLabel: ($) => $.advertising["Placement Report"],
  },
  AUDIENCE_REPORTS: {
    key: "audienceReports",
    groupKey: "audience_report",
    label: ($) => $.advertising["Audience Report"],
    groupLabel: ($) => $.advertising["Audience Report"],
  },
  AUTO_ADS: {
    key: "autoAds",
    groupKey: "auto_ads",
    label: ($) => $.advertising["Auto Ads"],
    groupLabel: ($) => $.advertising["Auto Ads"],
  },
  SEASONALITY: {
    key: "seasonality",
    groupKey: "configuration",
    label: ($) => $.advertising["Weekday Parting"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  SCHEDULED_CHANGES: {
    key: "scheduledChanges",
    groupKey: "configuration",
    label: ($) => $.advertising["Scheduled Updates"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  SEARCH_TERM_REPORT: {
    key: "searchTermReport",
    groupKey: "search_term_report",
    label: ($) => $.advertising["Search Term Report"],
    groupLabel: ($) => $.advertising["Search Term Report"],
  },
  DAY_PARTING: {
    key: "day_parting",
    groupKey: "configuration",
    label: ($) => $.advertising["Dayparting"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  DEBUG: {
    key: "debug",
    groupKey: "configuration",
    label: ($) => $.advertising["Debug"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  AUDIT_LOG: {
    key: "auditLog",
    groupKey: "configuration",
    label: ($) => $.advertising["Audit Log"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  CHANGE_LOG: {
    key: "changeLog",
    groupKey: "configuration",
    label: ($) => $.advertising["History"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  RULES: {
    key: "rules",
    groupKey: "configuration",
    label: ($) => $.advertising["Rules"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  HARVESTING_CONFIGURATION: {
    key: "harvesting",
    groupKey: "configuration",
    label: ($) => $.advertising["Harvesting"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  HARVESTING_RULES: {
    key: "harvestingRules",
    groupKey: "configuration",
    label: ($) => $.advertising["Harvesting Programs"],
    groupLabel: ($) => $.advertising["Configuration"],
  },
  HISTORY: {
    key: "history",
    groupKey: "history",
    label: ($) => $.advertising["History"],
    groupLabel: ($) => $.advertising["History"],
  },
} satisfies Record<string, TabOption>;

// Util to get applicable tabs for advertisement details based on media plan type and enabled modules
export const useTabsFromAdType = (
  adType: number,
  adGoal: number,
  isSegmented = false,
  isUnmanagedCategory = false,
  managed: string,
  adPlanParams: {
    nonTrellisEnrolledAd?: boolean;
    nonTrellisEnrolledAdAcceptBids?: boolean;
    audienceBidMultiplierTargetAudienceId?: boolean;
    autoCollection?: boolean;
  } = {},
  isArchivedAd = false,
) => {
  const hasNTMHarvestingModule = useModuleEnabled("EXTERNAL_HARVESTING");
  const hasDebugModule = useModuleEnabled("DEBUG");
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const isStaff = useAppSelector((state) => state.user.is_staff);
  let tabsToRender: TabOption[] = [];

  const ARCHIVED_TAB_GROUP_KEYS = ["configuration", "history"];
  // Auto Collection is keyword-targeting only, so it has no product target ad group to report on:
  // https://advertising.amazon.com/library/guides/sponsored-brands-collections
  const hideProductTargets = adPlanParams.autoCollection === true;
  const finalizeTabs = (tabs: TabOption[]) => {
    const visibleTabs =
      hideProductTargets ? tabs.filter((tab) => tab.key !== AD_DETAILS_TAB_OPTIONS.TARGETS.key) : tabs;
    return isArchivedAd && isStaff ?
        visibleTabs.filter((tab) => ARCHIVED_TAB_GROUP_KEYS.includes(tab.groupKey))
      : visibleTabs;
  };

  const {
    PRODUCTS,
    MEDIA_PLAN,
    KEYWORDS,
    TARGETS,
    NOTIFICATIONS,
    CAMPAIGN_PLACMENTS,
    AUDIENCE_REPORTS,
    AUTO_ADS,
    SERVING_HOURS,
    SEASONALITY,
    SCHEDULED_CHANGES,
    SEARCH_TERM_REPORT,
    CHANGE_LOG,
    DAY_PARTING,
    RULES,
    HARVESTING_CONFIGURATION,
    HARVESTING_RULES,
    DEBUG,
    AUDIT_LOG,
    HISTORY,
  } = AD_DETAILS_TAB_OPTIONS;

  if (adGoal === GOALS.IMPORTED_ADS.value) {
    if (channel === "walmart") {
      if ([AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(adType)) {
        tabsToRender = [PRODUCTS, KEYWORDS, MEDIA_PLAN, NOTIFICATIONS];
      } else {
        tabsToRender = [PRODUCTS, KEYWORDS, AUTO_ADS, MEDIA_PLAN, NOTIFICATIONS];
      }
      if (isStaff) tabsToRender.push(HISTORY);
      return finalizeTabs(tabsToRender);
    }

    tabsToRender = [PRODUCTS, TARGETS, MEDIA_PLAN, SERVING_HOURS, DAY_PARTING, RULES, SCHEDULED_CHANGES];

    if ([AD_TYPES.SPONSORED_DISPLAY.value, AD_TYPES.SPONSORED_DISPLAY_V2.value].includes(adType)) {
      if (!adPlanParams.nonTrellisEnrolledAd && !adPlanParams.nonTrellisEnrolledAdAcceptBids) {
        tabsToRender.splice(1, 1);
      }
    } else {
      tabsToRender.splice(1, 0, KEYWORDS);
    }
    if (adGoal === GOALS.IMPORTED_ADS.value && hasNTMHarvestingModule && managed === "Yes") {
      tabsToRender.push(HARVESTING_CONFIGURATION);
    }

    if ([AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(adType)) {
      tabsToRender.splice(-1, 0, CAMPAIGN_PLACMENTS);
      tabsToRender.push(HARVESTING_RULES);
    }

    if (
      [
        AD_TYPES.SPONSORED_PRODUCT.value,
        AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
        AD_TYPES.SPONSORED_BRAND.value,
        AD_TYPES.SPONSORED_BRAND_V2.value,
        AD_TYPES.SPONSORED_BRAND_VIDEO.value,
      ].includes(adType)
    ) {
      tabsToRender.splice(-1, 0, SEARCH_TERM_REPORT);
    }

    if (adPlanParams.audienceBidMultiplierTargetAudienceId) {
      tabsToRender.push(AUDIENCE_REPORTS);
    }

    if ([AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_V2.value].includes(adType)) {
      tabsToRender.splice(0, 1);
    }

    if (!isUnmanagedCategory) {
      tabsToRender.push(SEASONALITY);
    }

    if (hasDebugModule && channel === "amazon") {
      tabsToRender.push(DEBUG);
      tabsToRender.push(AUDIT_LOG);
    }

    if (isStaff) {
      tabsToRender.push(HISTORY);
    }

    return finalizeTabs(tabsToRender);
  }
  if (channel === "amazon") {
    switch (adType) {
      case AD_TYPES.SPONSORED_PRODUCT.value:
      case AD_TYPES.SPONSORED_PRODUCT_AUTO.value:
        tabsToRender = [PRODUCTS, KEYWORDS, TARGETS, CAMPAIGN_PLACMENTS, MEDIA_PLAN, NOTIFICATIONS, RULES];
        tabsToRender.push(HARVESTING_RULES);
        break;
      case AD_TYPES.CUSTOM.value:
        tabsToRender = [PRODUCTS, MEDIA_PLAN];
        break;
      case AD_TYPES.SPONSORED_BRAND.value:
      case AD_TYPES.SPONSORED_BRAND_V2.value:
      case AD_TYPES.SPONSORED_BRAND_VIDEO.value:
        tabsToRender = [KEYWORDS, TARGETS, MEDIA_PLAN, CAMPAIGN_PLACMENTS, NOTIFICATIONS, RULES];
        break;
      case AD_TYPES.SPONSORED_DISPLAY.value:
      case AD_TYPES.SPONSORED_DISPLAY_V2.value:
        tabsToRender = [PRODUCTS, TARGETS, MEDIA_PLAN, NOTIFICATIONS, RULES];
        break;
      default:
        tabsToRender = [];
        break;
    }
  }

  if (channel === "walmart") {
    if ([AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value].includes(adType)) {
      tabsToRender = [PRODUCTS, KEYWORDS, MEDIA_PLAN, NOTIFICATIONS];
    } else {
      tabsToRender = [PRODUCTS, KEYWORDS, AUTO_ADS, MEDIA_PLAN, NOTIFICATIONS];
    }
  }

  if (
    [
      AD_TYPES.SPONSORED_PRODUCT.value,
      AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
      AD_TYPES.SPONSORED_BRAND.value,
      AD_TYPES.SPONSORED_BRAND_V2.value,
      AD_TYPES.SPONSORED_BRAND_VIDEO.value,
      AD_TYPES.SPONSORED_PRODUCT_WALMART.value,
      AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value,
      AD_TYPES.SPONSORED_BRAND_WALMART.value,
      AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
    ].includes(adType) &&
    // Components with parent have number set in segmented flag
    isSegmented !== true
  ) {
    tabsToRender.push(SEARCH_TERM_REPORT);
  }

  if (channel === "amazon") {
    tabsToRender.push(SEASONALITY);
    tabsToRender.push(DAY_PARTING);
  }

  if (!isSegmented) {
    tabsToRender.push(SCHEDULED_CHANGES);
  }

  if (adPlanParams.audienceBidMultiplierTargetAudienceId) {
    tabsToRender.push(AUDIENCE_REPORTS);
  }

  // Below address shared ad type value between Walmart Sponsored Product and Segmented parent ad values
  if ((adType !== AD_TYPES.CUSTOM.value && adGoal !== GOALS.IMPORTED_ADS.value) || channel === "walmart") {
    tabsToRender.push(CHANGE_LOG);
  }

  if (!(adType === AD_TYPES.CUSTOM.value && channel === "amazon" && adGoal !== GOALS.IMPORTED_ADS.value)) {
    tabsToRender.splice(-2, 0, SERVING_HOURS);
  }

  if (hasDebugModule && channel === "amazon") {
    tabsToRender.push(DEBUG);
    tabsToRender.push(AUDIT_LOG);
  }

  if (isStaff) {
    tabsToRender.push(HISTORY);
  }

  return finalizeTabs(tabsToRender);
};
